// imports
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';

// local imports
import CustomInput from '../../../CustomInput';
import ColorPickerPopover from '../../../ColorPickerPopover';
import {
  defaultPointColor,
  GeoJsonAdditionalLayer,
  MVTAdditionalLayer,
} from '../../../../../configs/additionalLayers';
import { mapActions } from '../../../../../store';

interface PointLayerControlsProps {
  layer: MVTAdditionalLayer | GeoJsonAdditionalLayer;
}

/**
 * PointLayerControls
 *
 * Renders the point color and point size controls for the vector data with geometryType 'point'
 *
 * @param layer - The additionalLayer object
 * @returns {JSX.Element} The rendered point color and size controls.
 */
export default function PointLayerControls({ layer }: PointLayerControlsProps) {
  const dispatch = useDispatch();
  const [localPointColor, setLocalPointColor] = useState<string>(
    layer.pointColor || defaultPointColor
  );

  useEffect(() => {
    dispatch(
      mapActions.setPointColor({
        id: layer.id,
        pointColor: localPointColor,
      })
    );
  }, [localPointColor, layer.id, dispatch]);

  const handlePointSizeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    if (newValue === '') {
      dispatch(mapActions.setPointSize({ id: layer.id, pointSize: '' }));
      return;
    }
    if (
      isNaN(Number(newValue)) ||
      Number(newValue) < 1 ||
      Number(newValue) > 99
    ) {
      return;
    }
    dispatch(
      mapActions.setPointSize({ id: layer.id, pointSize: Number(newValue) })
    );
  };

  return (
    <Box data-testid="point-layer-controls-container" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#555', letterSpacing: '0.05em' }}>
        Color
      </Typography>
      <Box sx={{ display: 'flex', width: '100%', gap: 1, alignItems: 'center' }}>
        <ColorPickerPopover
          color={localPointColor}
          onChange={setLocalPointColor}
          data-testid="point-color-picker"
          aria-label="Point color"
        />
        <CustomInput
          data-testid="point-size-picker"
          value={layer.pointSize || ''}
          onChange={handlePointSizeChange}
          width="80px"
          suffix="px"
          type="number"
          min="1"
          max="99"
          aria-label="Point size in pixels"
        />
      </Box>
    </Box>
  );
}
